import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { assetGroupActions } from './slice';
import { accessActions } from 'reduxStore/access/slice';
import { watchActivity } from '../_shared/activity';

// Fetches groups for the active scope (orgID + siteID from selectedProfile).
// Payload can override siteID — used by Add Visitor / Add Person to force the 'all' bucket.
export function* loadGroups({ payload } = {}) {
	const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
	const siteID = payload?.siteID ?? profileSiteID;
	const query = siteID !== 'all' ? { siteID } : {};

	// Set loading on target bucket (no saga listens to loadGroupsStart — no loop)
	yield put(assetGroupActions.loadGroupsStart({ orgID, siteID }));

	try {
		const assetGroups = yield call(() => SuperFetch.get('/deviceGroups', {}, query));

		if (assetGroups.statusCode === 200) {
			yield put(assetGroupActions.loadGroupsSuccess({ orgID, siteID, data: assetGroups.data }));
		} else {
			throw Error(assetGroups.error.message || assetGroups.error || 'Failed to load Groups.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadGroupsError({ orgID, siteID, error: error.message }));
	}
}

// 🔥 Actions after load groups success
export function* loadGroupsSuccess() {
	yield put(assetGroupActions.loadAssetsGroups());
	yield put(assetGroupActions.loadGroupStructure());
}

// 🔥 Load group-nesting structure (org-scoped, site-invariant). Assembled into a byGroupID map the
// permission display recurses over. See docs/features/group-nesting/phase-f-frontend.md.
export function* loadGroupStructure() {
	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	yield put(assetGroupActions.loadGroupStructureStart({ orgID }));
	try {
		const res = yield call(() => SuperFetch.get('/deviceGroups/tree'));
		if (res.statusCode === 200) {
			const byGroupID = Object.fromEntries((res.data || []).map((node) => [node.groupID, node]));
			yield put(assetGroupActions.loadGroupStructureSuccess({ orgID, byGroupID }));
		} else {
			throw new Error(res.error?.message || res.error || 'Failed to load group structure.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadGroupStructureError({ orgID, error: error.message }));
	}
}

export function* loadAssetsGroups(action) {
	try {
		// Get the orgID from the selected profile
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile); // Get orgID

		// Get the assets of the group
		const assets = yield call(() => SuperFetch.get(`/deviceGroups/getAllDeviceGroupsAllDevices`));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.loadAssetsGroupsSuccess({ orgID, siteID, data: assets.data }));
		} else {
			throw Error(assets.error.message || assets.error || 'Failed to load group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadAssetsGroupsError(error));
	}
}

export function* addGroup(action) {
	try {
		const { data } = action.payload;
		const { assetIDs, ...groupData } = data; // Separate assetIDs if present

		// 1️⃣ Create the device group
		const assetGroup = yield call(() => SuperFetch.post(`/deviceGroups`, groupData));

		if (assetGroup.statusCode === 201 || assetGroup.statusCode === 200) {
			const { groupID } = assetGroup.data;

			yield put(assetGroupActions.addGroupSuccess());

			Notification('success', 'Group created', 'Device Group created.');

			// 2️⃣ If assetIDs exist, dispatch action to assign them to the group
			if (Array.isArray(assetIDs) && assetIDs.length > 0) {
				yield put(assetGroupActions.addGroupAsset({ groupID, assetIDs }));
			}
		} else {
			throw new Error(
				assetGroup.error?.message || assetGroup.error || 'Failed to create Device Group. Please try again.'
			);
		}
	} catch (error) {
		yield put(assetGroupActions.addGroupError(error));
		Notification('error', 'Group failed', error.message);
	}
}

// 🔥Actions here after add asset group success
export function* addGroupSuccess() {
	yield put(assetGroupActions.refreshGroups());
}

export function* updateGroup(action) {
	const { groupID, data } = action.payload;

	try {
		// Update the group
		const assetGroup = yield call(() => SuperFetch.patch(`/deviceGroups/${groupID}`, data));

		if (assetGroup.statusCode === 200) {
			yield put(assetGroupActions.updateGroupSuccess());

			// Show success notification
			Notification('success', 'Group updated', 'Group has been updated successfully.');
		} else {
			throw Error(assetGroup.error.message || assetGroup.error || 'Failed to update group.');
		}
	} catch (error) {
		yield put(assetGroupActions.updateGroupError(error));
		Notification('error', 'Update failed', error.message);
	}
}

// 🔥Actions here after update Group
export function* updateGroupSuccess() {
	yield put(assetGroupActions.refreshGroups());
}

// Silent refresh — re-fetches groups without setting bucket loading flag
export function* refreshGroups() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID !== 'all' ? { siteID } : {};
		const assetGroups = yield call(() => SuperFetch.get('/deviceGroups', {}, query));

		if (assetGroups.statusCode === 200) {
			yield put(assetGroupActions.refreshGroupsSuccess({ orgID, siteID, data: assetGroups.data }));
		}
	} catch (_) {
		// Silent — no error notification
	}
}

// ✅ Handle single or multiple group deletions via the bulk endpoint.
// The backend deletes each group independently — one failure doesn't abort the rest,
// so the response reports per-group results plus a { deleted, failed } summary.
export function* deleteGroup(action) {
	const { groupIDs } = action.payload;

	try {
		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < groupIDs.length; i += 50) chunks.push(groupIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/deviceGroups/bulk', { groupIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || 'Failed to delete group(s).');
			}
			const summary = res.data?.summary || res.summary || {};
			deleted += summary.deleted || 0;
			failed += summary.failed || 0;
		});

		if (deleted === 0 && failed > 0) {
			throw Error(`Failed to delete ${failed} group${failed > 1 ? 's' : ''}.`);
		}

		yield put(assetGroupActions.deleteGroupSuccess());

		if (failed > 0) {
			Notification('warning', 'Some groups were not deleted', `${deleted} deleted, ${failed} failed.`);
		} else {
			Notification(
				'success',
				deleted > 1 ? 'Groups deleted' : 'Group deleted',
				deleted > 1 ? `${deleted} groups have been permanently deleted.` : 'Group has been permanently deleted.'
			);
		}
	} catch (error) {
		const errMsg = error?.message || String(error) || 'Failed to delete group(s).';
		yield put(assetGroupActions.deleteGroupError({ message: errMsg }));
		Notification('error', 'Delete failed', errMsg);
	}
}

// 🔥 Actions after delete group
export function* deleteGroupSuccess() {
	yield put(assetGroupActions.refreshGroups());
	yield put(accessActions.refreshAccessPermissions());
}

// Fetch assets for a group — patches inside the active scope bucket
export function* loadGroupAssets(action) {
	try {
		// Get the groupID from the action payload
		const { groupID } = action.payload;

		// Get the orgID from the selected profile
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile); // Get orgID

		// Get the assets of the group
		const assets = yield call(() => SuperFetch.get(`/deviceGroups/${groupID}/assets`));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.loadGroupAssetsSuccess({ orgID, siteID, groupID, data: assets.data }));
		} else {
			throw Error(assets.error.message || assets.error || 'Failed to load group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.loadGroupAssetsError(error));
	}
}

export function* addGroupAsset(action) {
	try {
		const { groupID, assetIDs } = action.payload;

		// 1️⃣ Add the assets to the group
		const assets = yield call(() => SuperFetch.post(`/deviceGroups/${groupID}/assets`, { assetIDs }));

		// 2️⃣ If successful, dispatch success action and show notification
		if (assets.statusCode === 201 || assets.statusCode === 200) {
			yield put(assetGroupActions.addGroupAssetSuccess({ groupID }));

			const filtered = assets.data?.filtered || 0;
			if (filtered > 0 && filtered === assetIDs.length) {
				Notification(
					'warning',
					'No devices added',
					`${filtered} device(s) skipped — their location doesn't match the group's site.`
				);
			} else if (filtered > 0) {
				Notification(
					'warning',
					'Partially added',
					`Some devices were added, but ${filtered} device(s) skipped — their location doesn't match the group's site.`
				);
			} else {
				Notification('success', 'Asset added', 'Devices added to Device Groups successfully.');
			}
		} else {
			throw new Error(assets?.error?.message || assets?.error || 'Failed to add devices. Please try again.');
		}
	} catch (error) {
		yield put(assetGroupActions.addGroupAssetError(error));
		Notification('error', 'Asset failed', error.message);
	}
}

// 🔥Actions here after Add asset into the Group
export function* addGroupAssetSuccess(action) {
	const { groupID } = action.payload;
	yield put(assetGroupActions.refreshGroupAssets({ groupID }));
	yield put(accessActions.refreshAccessPermissions());
}

export function* removeGroupAsset(action) {
	try {
		const { groupID, assetIDs } = action.payload;

		// API request to delete the assets from group
		const assets = yield call(() => SuperFetch.delete(`/deviceGroups/${groupID}/assets`, { assetIDs }));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.removeGroupAssetSuccess({ groupID }));

			// ✅ Success Notification
			Notification('success', 'Asset removed', 'Asset(s) have been removed from the group successfully.');
		} else {
			throw Error(assets?.error?.message || 'Failed to delete group assets.');
		}
	} catch (error) {
		yield put(assetGroupActions.removeGroupAssetError(error));
		Notification('error', 'Remove failed', error.message);
	}
}

// 🔥Actions here after delete assets from group
export function* removeGroupAssetSuccess(action) {
	const { groupID } = action.payload;

	yield put(assetGroupActions.refreshGroupAssets({ groupID }));
	yield put(accessActions.refreshAccessPermissions());
}

// Silent refresh — re-fetches group assets without setting loading flag
export function* refreshGroupAssets(action) {
	try {
		const { groupID } = action.payload;
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const assets = yield call(() => SuperFetch.get(`/deviceGroups/${groupID}/assets`));

		if (assets.statusCode === 200) {
			yield put(assetGroupActions.refreshGroupAssetsSuccess({ orgID, siteID, groupID, data: assets.data }));
		}
	} catch (_) {
		// Silent — no error notification
	}
}

// ── Group nesting: link/unlink child groups ─────────────────────────────
// Batch POST — the backend links each child individually and returns per-child results
// ({ childGroupID, success, error? }); an all-rejected request comes back 400.
export function* linkChildGroups(action) {
	try {
		const { groupID, childGroupIDs } = action.payload;

		const res = yield call(() => SuperFetch.post(`/deviceGroups/${groupID}/childGroups`, { childGroupIDs }));

		if (res.statusCode === 200) {
			yield put(assetGroupActions.linkChildGroupsSuccess({ groupID }));

			const failed = (res.data?.results || []).filter((r) => !r.success);
			if (failed.length > 0) {
				const detail = failed.map((f) => f.error || `Group ${f.childGroupID} skipped.`).join(' ');
				Notification('warning', 'Some groups not linked', detail);
			} else {
				Notification('success', 'Groups linked', 'Nested groups added successfully.');
			}
		} else {
			const err = res?.error;
			throw new Error(
				err?.message || (Array.isArray(err) ? err.join(' ') : err) || 'Failed to link nested groups.'
			);
		}
	} catch (error) {
		yield put(assetGroupActions.linkChildGroupsError(error));
		Notification('error', 'Link failed', error.message);
	}
}

// 🔥 After link — refresh nesting structure, groups and permissions (fan-out changed grants).
export function* linkChildGroupsSuccess() {
	yield put(assetGroupActions.loadGroupStructure());
	yield put(assetGroupActions.refreshGroups());
	yield put(accessActions.refreshAccessPermissions());
}

export function* unlinkChildGroups(action) {
	try {
		const { groupID, childGroupIDs } = action.payload;

		const res = yield call(() => SuperFetch.delete(`/deviceGroups/${groupID}/childGroups`, { childGroupIDs }));

		if (res.statusCode === 200) {
			yield put(assetGroupActions.unlinkChildGroupsSuccess({ groupID }));
			Notification('success', 'Groups unlinked', 'Nested groups removed successfully.');
		} else {
			throw new Error(res?.error?.message || res?.error || 'Failed to unlink nested groups.');
		}
	} catch (error) {
		yield put(assetGroupActions.unlinkChildGroupsError(error));
		Notification('error', 'Unlink failed', error.message);
	}
}

// 🔥 After unlink — refresh nesting structure, groups and permissions (fan-out revoked grants).
export function* unlinkChildGroupsSuccess() {
	yield put(assetGroupActions.loadGroupStructure());
	yield put(assetGroupActions.refreshGroups());
	yield put(accessActions.refreshAccessPermissions());
}

// ── PermissionGroup→List edge schedule (unified-schedule-expiry, IR-only) ──
// PATCH writes the <When> to IR then mirrors to the Zezamii edge; refresh the structure tree
// so the (read) childGroupSchedules reflect the new value.
export function* updateChildGroupSchedule(action) {
	try {
		const { groupID, childGroupID, accessWindowID } = action.payload;
		const res = yield call(() =>
			SuperFetch.patch(`/deviceGroups/${groupID}/childGroups/${childGroupID}/schedule`, { accessWindowID })
		);
		if (res.statusCode === 200) {
			yield put(assetGroupActions.updateChildGroupScheduleSuccess());
			yield put(assetGroupActions.loadGroupStructure());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Schedule updated', 'List schedule saved to Integriti.');
		} else {
			const err = res?.error;
			throw new Error(err?.message || (Array.isArray(err) ? err.join(' ') : err) || 'Failed to update schedule.');
		}
	} catch (error) {
		yield put(assetGroupActions.updateChildGroupScheduleError(error.message));
		Notification('error', 'Schedule update failed', error.message);
	}
}

// ── Combined List link/unlink + schedule (IR "Manage lists" modal) ──
// Sequential so a newly-linked List's schedule is applied AFTER its edge exists.
// payload: { groupID, toLink:[ids], toUnlink:[ids], schedules:[{ childGroupID, accessWindowID|null }] }
export function* updateGroupLists(action) {
	try {
		const { groupID, toLink, toUnlink, schedules } = action.payload;
		if (toUnlink?.length) {
			yield call(() => SuperFetch.delete(`/deviceGroups/${groupID}/childGroups`, { childGroupIDs: toUnlink }));
		}
		if (toLink?.length) {
			const res = yield call(() =>
				SuperFetch.post(`/deviceGroups/${groupID}/childGroups`, { childGroupIDs: toLink })
			);
			if (res.statusCode !== 200) {
				const err = res?.error;
				throw new Error(err?.message || (Array.isArray(err) ? err.join(' ') : err) || 'Failed to link lists.');
			}
		}
		for (const s of schedules || []) {
			const res = yield call(() =>
				SuperFetch.patch(`/deviceGroups/${groupID}/childGroups/${s.childGroupID}/schedule`, {
					accessWindowID: s.accessWindowID
				})
			);
			if (res.statusCode !== 200) {
				const err = res?.error;
				throw new Error(
					err?.message || (Array.isArray(err) ? err.join(' ') : err) || 'Failed to set list schedule.'
				);
			}
		}
		yield put(assetGroupActions.updateGroupListsSuccess());
		yield put(assetGroupActions.loadGroupStructure());
		yield put(assetGroupActions.refreshGroups());
		yield put(accessActions.refreshAccessPermissions());
		Notification('success', 'Lists updated', 'List changes saved to Integriti.');
	} catch (error) {
		yield put(assetGroupActions.updateGroupListsError(error.message));
		Notification('error', 'Update failed', error.message);
	}
}

// Root saga
export default function* assetGroupSagas() {
	yield takeLatest(assetGroupActions.loadGroups, loadGroups);
	yield takeLatest(assetGroupActions.loadGroupsSuccess, loadGroupsSuccess);
	yield takeLatest(assetGroupActions.loadAssetsGroups, loadAssetsGroups);
	yield takeLatest(assetGroupActions.loadGroupStructure, loadGroupStructure);
	yield takeLatest(assetGroupActions.addGroup, addGroup);
	yield takeLatest(assetGroupActions.addGroupSuccess, addGroupSuccess);
	yield takeLatest(assetGroupActions.updateGroup, updateGroup);
	yield takeLatest(assetGroupActions.updateGroupSuccess, updateGroupSuccess);
	yield takeLatest(assetGroupActions.deleteGroup, deleteGroup);
	yield takeLatest(assetGroupActions.deleteGroupSuccess, deleteGroupSuccess);
	yield takeLatest(assetGroupActions.loadGroupAssets, loadGroupAssets);
	yield takeLatest(assetGroupActions.addGroupAsset, addGroupAsset);
	yield takeLatest(assetGroupActions.addGroupAssetSuccess, addGroupAssetSuccess);
	yield takeLatest(assetGroupActions.removeGroupAsset, removeGroupAsset);
	yield takeLatest(assetGroupActions.removeGroupAssetSuccess, removeGroupAssetSuccess);
	yield takeLatest(assetGroupActions.refreshGroupAssets, refreshGroupAssets);
	yield takeLatest(assetGroupActions.refreshGroups, refreshGroups);
	yield takeLatest(assetGroupActions.linkChildGroups, linkChildGroups);
	yield takeLatest(assetGroupActions.linkChildGroupsSuccess, linkChildGroupsSuccess);
	yield takeLatest(assetGroupActions.unlinkChildGroups, unlinkChildGroups);
	yield takeLatest(assetGroupActions.unlinkChildGroupsSuccess, unlinkChildGroupsSuccess);
	yield takeLatest(assetGroupActions.updateChildGroupSchedule, updateChildGroupSchedule);
	yield takeLatest(assetGroupActions.updateGroupLists, updateGroupLists);
	yield* watchActivity(assetGroupActions);
}
